<!-- eslint-disable vue/multi-word-component-names -->
<template>
  <section class="team">
    <div class="container">
      <!-- 标题区：与公司介绍协同 -->
      <div class="section__header">
        <p class="team__en">OUR TEAM</p>
        <p class="section__subtitle">专业、温暖、可靠，来自医药与互联网领域的核心团队</p>
      </div>

      <div v-if="loading" class="team__loading">加载中...</div>
      <div v-else-if="error" class="team__error">{{ error }}</div>

      <div v-else-if="members.length" class="team__carousel">
        <button
            class="team__arrow team__arrow--prev"
            @click="prev"
            :disabled="members.length <= 1"
            aria-label="上一位"
        >
          <svg viewBox="0 0 24 24" width="24" height="24">
            <path d="M15 6l-6 6 6 6" stroke="currentColor" stroke-width="2" fill="none" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </button>

        <transition name="fade" mode="out-in">
          <div class="team__slide" :key="current.id">
            <!-- 左照片 -->
            <div class="team__photo">
              <img :src="current.avatar" :alt="current.name" />
            </div>

            <!-- 右信息 -->
            <div class="team__info">
              <p class="team__position">{{ current.position }}</p>
              <h3 class="team__name">{{ current.name }}</h3>
              <p class="team__brief">{{ current.brief }}</p>

              <!-- 数字亮点（如顾旖） -->
              <div
                  v-if="current.highlights && current.highlights.length"
                  class="team__highlights"
              >
                <div
                    v-for="(h, i) in current.highlights"
                    :key="i"
                    class="team__highlight"
                >
                  <strong>{{ h.value }}</strong>
                  <span>{{ h.label }}</span>
                </div>
              </div>

              <!-- 履历分类：两列铺开 -->
              <div class="team__sections">
                <div
                    v-for="(section, i) in current.sections"
                    :key="i"
                    class="team__section"
                >
                  <h4 class="team__section-title">{{ section.title }}</h4>
                  <ul class="team__section-list">
                    <li v-for="(item, j) in section.items" :key="j">{{ item }}</li>
                  </ul>
                </div>
              </div>

              <button
                  class="btn btn--outline btn--md team__more"
                  @click="openDetail(current)"
              >
                查看详情
              </button>
            </div>
          </div>
        </transition>

        <button
            class="team__arrow team__arrow--next"
            @click="next"
            :disabled="members.length <= 1"
            aria-label="下一位"
        >
          <svg viewBox="0 0 24 24" width="24" height="24">
            <path d="M9 6l6 6-6 6" stroke="currentColor" stroke-width="2" fill="none" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </button>

        <div class="team__dots">
          <button
              v-for="(m, i) in members"
              :key="m.id"
              class="team__dot"
              :class="{ 'team__dot--active': i === currentIndex }"
              @click="goTo(i)"
              :aria-label="`第${i + 1}位`"
          ></button>
        </div>
      </div>

      <div v-else class="team__empty">暂无团队成员</div>
    </div>

    <!-- 详情弹窗：加大 -->
    <transition name="modal">
      <div v-if="detailVisible" class="team-modal" @click.self="closeDetail">
        <div class="team-modal__panel">
          <button class="team-modal__close" @click="closeDetail" aria-label="关闭">×</button>

          <div class="team-modal__body">
            <div class="team-modal__photo">
              <img :src="detail.avatar" :alt="detail.name" />
              <p class="team-modal__photo-position">{{ detail.position }}</p>
              <h3 class="team-modal__photo-name">{{ detail.name }}</h3>
            </div>

            <div class="team-modal__content">
              <p v-if="detail.brief" class="team-modal__brief">{{ detail.brief }}</p>

              <div
                  v-if="detail.highlights && detail.highlights.length"
                  class="team-modal__highlights"
              >
                <div
                    v-for="(h, i) in detail.highlights"
                    :key="i"
                    class="team-modal__highlight"
                >
                  <strong>{{ h.value }}</strong>
                  <span>{{ h.label }}</span>
                </div>
              </div>

              <div
                  v-for="(section, i) in detail.sections"
                  :key="i"
                  class="team-modal__section"
              >
                <h4 class="team-modal__section-title">{{ section.title }}</h4>
                <ul class="team-modal__list">
                  <li v-for="(item, j) in section.items" :key="j">{{ item }}</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </transition>
  </section>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, watch } from 'vue'

const API_URL = 'https://lvtunpeizhen.com:8488/admin/website/team_user'
const AUTH_TOKEN = 'OpmlVJhrZJn6w'

const members = ref([])
const loading = ref(true)
const error = ref('')

const currentIndex = ref(0)
const current = computed(() => members.value[currentIndex.value] || {})

const detailVisible = ref(false)
const detail = ref({})

const safeParse = (str) => {
  if (!str) return []
  if (typeof str === 'object') return str
  try {
    return JSON.parse(str) || []
  } catch {
    return []
  }
}

const normalizeMember = (m) => ({
  ...m,
  highlights: safeParse(m.highlights),
  sections: safeParse(m.sections)
})

const fetchMembers = async () => {
  loading.value = true
  error.value = ''
  try {
    const res = await fetch(API_URL, {
      headers: { 'AUTH-TOKEN': AUTH_TOKEN }
    })
    const json = await res.json()
    if (!json.success) throw new Error(json.message || '接口返回失败')
    members.value = (json.data || []).map(normalizeMember)
  } catch (e) {
    console.error(e)
    error.value = '团队成员加载失败，请稍后再试'
  } finally {
    loading.value = false
  }
}

const next = () => {
  if (members.value.length <= 1) return
  currentIndex.value = (currentIndex.value + 1) % members.value.length
}
const prev = () => {
  if (members.value.length <= 1) return
  currentIndex.value = (currentIndex.value - 1 + members.value.length) % members.value.length
}
const goTo = (i) => {
  currentIndex.value = i
}

const openDetail = (member) => {
  detail.value = member
  detailVisible.value = true
}
const closeDetail = () => {
  detailVisible.value = false
}

watch(detailVisible, (v) => {
  document.body.style.overflow = v ? 'hidden' : ''
})

const onKey = (e) => {
  if (e.key === 'Escape' && detailVisible.value) closeDetail()
}

onMounted(() => {
  fetchMembers()
  window.addEventListener('keydown', onKey)
})
onUnmounted(() => {
  window.removeEventListener('keydown', onKey)
  document.body.style.overflow = ''
})
</script>

<style scoped>
/* ===================== 外层：白底，与公司介绍一致 ===================== */
.team {
  position: relative;
  background: #ffffff;
  padding: 80px 0 100px;
}

.container {
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 24px;
}

/* ===================== 标题区：与公司介绍协同 ===================== */
.section__header {
  text-align: center;
  margin-bottom: 48px;
}
.team__en {
  font-size: 14px;
  letter-spacing: 4px;
  color: #8a9aa8;
  margin-bottom: 12px;
}
.section__title {
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: 14px;
  font-size: clamp(30px, 3.2vw, 40px);
  font-weight: 800;
  color: var(--gt-primary);
  margin-bottom: 12px;
}
/* 标题左侧蓝色竖条，呼应公司介绍的色块 */
.team__title-bar {
  display: inline-block;
  width: 6px;
  height: 32px;
  background: linear-gradient(180deg, var(--gt-primary), var(--gt-secondary));
  border-radius: 3px;
}
.section__subtitle {
  font-size: 16px;
  color: #4a5a6a;
  max-width: 640px;
  margin: 0 auto;
}

/* ===================== 轮播：加宽到容器边缘 ===================== */
.team__carousel {
  position: relative;
  max-width: 1320px;
  margin: 0 auto;
  padding: 10px 72px 60px;
}

/* 卡片：左照片 + 右信息 */
.team__slide {
  display: grid;
  grid-template-columns: 300px 1fr;
  gap: 44px;
  background: #ffffff;
  border-radius: 16px;
  padding: 40px;
  border: 1px solid #e8eef5;
  box-shadow: 0 12px 40px rgba(26, 111, 181, 0.08);
  min-height: 520px;
}

/* 照片 */
.team__photo {
  width: 100%;
  border-radius: 12px;
  overflow: hidden;
  aspect-ratio: 3 / 4;
  background: #e8f2fb;
  align-self: flex-start;
}
.team__photo img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

/* 右侧信息 */
.team__info {
  display: flex;
  flex-direction: column;
}
.team__position {
  font-size: 16px;
  color: var(--gt-primary);
  font-weight: 600;
  letter-spacing: 1px;
  margin-bottom: 10px;
}
.team__name {
  font-size: 36px;
  font-weight: 800;
  color: #1f2d3d;
  margin-bottom: 18px;
  line-height: 1.25;
}
.team__brief {
  font-size: 17px;
  color: #4a5a6a;
  line-height: 1.9;
  margin-bottom: 24px;
}

/* 数字亮点 */
.team__highlights {
  display: flex;
  gap: 36px;
  margin-bottom: 26px;
  padding-bottom: 22px;
  border-bottom: 1px solid #e8eef5;
  flex-wrap: wrap;
}
.team__highlight {
  display: flex;
  flex-direction: column;
}
.team__highlight strong {
  font-size: 28px;
  font-weight: 800;
  color: var(--gt-primary);
  line-height: 1.2;
  margin-bottom: 4px;
}
.team__highlight span {
  font-size: 13px;
  color: #4a5a6a;
}

/* 履历：两列铺开 */
.team__sections {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 22px 36px;
  margin-bottom: 28px;
  flex: 1;
}
.team__section-title {
  font-size: 15px;
  font-weight: 700;
  color: #1f2d3d;
  margin-bottom: 10px;
  padding-left: 10px;
  border-left: 3px solid var(--gt-primary);
  line-height: 1.4;
}
.team__section-list {
  list-style: none;
  padding: 0;
  margin: 0;
}
.team__section-list li {
  position: relative;
  padding-left: 14px;
  font-size: 14px;
  color: #4a5a6a;
  line-height: 1.8;
  margin-bottom: 4px;
}
.team__section-list li::before {
  content: '';
  position: absolute;
  left: 0;
  top: 9px;
  width: 5px;
  height: 5px;
  border-radius: 50%;
  background: var(--gt-secondary);
  opacity: 0.7;
}

.team__more {
  align-self: flex-start;
  margin-top: auto;
}

/* 箭头 */
.team__arrow {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  width: 52px;
  height: 52px;
  border-radius: 50%;
  border: 1.5px solid var(--gt-primary);
  background: #fff;
  color: var(--gt-primary);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.25s;
  z-index: 3;
}
.team__arrow:hover:not(:disabled) {
  background: var(--gt-primary);
  color: #fff;
  transform: translateY(-50%) scale(1.06);
  box-shadow: 0 8px 24px rgba(26, 111, 181, 0.25);
}
.team__arrow:disabled {
  opacity: 0.3;
  cursor: not-allowed;
}
.team__arrow--prev { left: 0; }
.team__arrow--next { right: 0; }

/* 指示点 */
.team__dots {
  position: absolute;
  left: 50%;
  bottom: 16px;
  transform: translateX(-50%);
  display: flex;
  gap: 10px;
}
.team__dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: #cbd5e1;
  border: none;
  padding: 0;
  cursor: pointer;
  transition: all 0.25s;
}
.team__dot--active {
  background: var(--gt-primary);
  width: 26px;
  border-radius: 6px;
}

/* 状态 */
.team__loading,
.team__error,
.team__empty {
  text-align: center;
  padding: 60px 0;
  color: #8a9aa8;
  font-size: 15px;
}

/* 切换动画 */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease, transform 0.3s ease;
}
.fade-enter-from {
  opacity: 0;
  transform: translateX(24px);
}
.fade-leave-to {
  opacity: 0;
  transform: translateX(-24px);
}

/* ===================== 详情弹窗：加大 ===================== */
.team-modal {
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.6);
  backdrop-filter: blur(6px);
  z-index: 2000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 40px 24px;
}
.team-modal__panel {
  position: relative;
  background: #fff;
  border-radius: 20px;
  width: 100%;
  max-width: 1200px;
  max-height: 88vh;
  overflow: hidden;
  box-shadow: 0 30px 80px rgba(0, 0, 0, 0.3);
}
.team-modal__close {
  position: absolute;
  top: 20px;
  right: 24px;
  width: 42px;
  height: 42px;
  border-radius: 50%;
  border: none;
  background: #f1f5f9;
  color: #475569;
  font-size: 24px;
  line-height: 1;
  cursor: pointer;
  transition: all 0.2s;
  z-index: 2;
}
.team-modal__close:hover {
  background: var(--gt-primary);
  color: #fff;
}

.team-modal__body {
  display: grid;
  grid-template-columns: 360px 1fr;
  max-height: 88vh;
}

/* 弹窗左照片 */
.team-modal__photo {
  background: linear-gradient(180deg, #e8f2fb 0%, #e6f5f3 100%);
  padding: 56px 32px 32px;
  text-align: center;
}
.team-modal__photo img {
  width: 100%;
  aspect-ratio: 3 / 4;
  object-fit: cover;
  border-radius: 14px;
  box-shadow: 0 12px 32px rgba(26, 111, 181, 0.2);
  margin-bottom: 24px;
}
.team-modal__photo-position {
  font-size: 15px;
  color: var(--gt-primary);
  font-weight: 600;
  letter-spacing: 1px;
  margin-bottom: 8px;
}
.team-modal__photo-name {
  font-size: 28px;
  font-weight: 800;
  color: #1f2d3d;
}

/* 弹窗右内容 */
.team-modal__content {
  padding: 60px 52px 48px;
  overflow-y: auto;
  max-height: 88vh;
}
.team-modal__brief {
  font-size: 17px;
  color: #4a5a6a;
  line-height: 1.9;
  margin-bottom: 28px;
  padding-bottom: 24px;
  border-bottom: 1px solid #e8eef5;
}

/* 数字亮点 */
.team-modal__highlights {
  display: flex;
  gap: 40px;
  margin-bottom: 36px;
  padding-bottom: 28px;
  border-bottom: 1px solid #e8eef5;
  flex-wrap: wrap;
}
.team-modal__highlight {
  display: flex;
  flex-direction: column;
}
.team-modal__highlight strong {
  font-size: 36px;
  color: var(--gt-primary);
  font-weight: 800;
  line-height: 1.2;
  margin-bottom: 6px;
}
.team-modal__highlight span {
  font-size: 14px;
  color: #4a5a6a;
}

/* 履历分类 */
.team-modal__section {
  margin-bottom: 32px;
}
.team-modal__section:last-child {
  margin-bottom: 0;
}
.team-modal__section-title {
  font-size: 18px;
  font-weight: 700;
  color: #1f2d3d;
  margin-bottom: 16px;
  padding-left: 14px;
  border-left: 4px solid var(--gt-primary);
  line-height: 1.4;
}
.team-modal__list {
  list-style: none;
  padding: 0;
  margin: 0;
}
.team-modal__list li {
  position: relative;
  padding-left: 20px;
  font-size: 15px;
  color: #4a5a6a;
  line-height: 1.9;
  margin-bottom: 8px;
}
.team-modal__list li::before {
  content: '';
  position: absolute;
  left: 0;
  top: 12px;
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: var(--gt-secondary);
  opacity: 0.7;
}

/* 弹窗动画 */
.modal-enter-active,
.modal-leave-active {
  transition: opacity 0.25s ease;
}
.modal-enter-active .team-modal__panel,
.modal-leave-active .team-modal__panel {
  transition: transform 0.3s ease, opacity 0.3s ease;
}
.modal-enter-from,
.modal-leave-to {
  opacity: 0;
}
.modal-enter-from .team-modal__panel,
.modal-leave-to .team-modal__panel {
  transform: translateY(24px) scale(0.97);
  opacity: 0;
}

/* ===================== 响应式 ===================== */
@media (max-width: 1100px) {
  .team__slide {
    grid-template-columns: 260px 1fr;
    gap: 32px;
    padding: 32px;
    min-height: auto;
  }
  .team__name { font-size: 30px; }
  .team__brief { font-size: 16px; }
  .team__sections { gap: 18px 28px; }
  .team__section-list li { font-size: 13px; }
}

@media (max-width: 900px) {
  .team { padding: 60px 0; }
  .team__carousel { padding: 10px 0 60px; }
  .team__slide {
    grid-template-columns: 1fr;
    gap: 24px;
    padding: 24px;
  }
  .team__photo {
    max-width: 220px;
    margin: 0 auto;
    aspect-ratio: 1 / 1;
  }
  .team__name { font-size: 26px; }
  .team__sections { grid-template-columns: 1fr; gap: 18px; }
  .team__arrow {
    width: 42px;
    height: 42px;
    top: auto;
    bottom: 14px;
    transform: none;
  }
  .team__arrow:hover:not(:disabled) { transform: scale(1.06); }
  .team__arrow--prev { left: calc(50% - 90px); }
  .team__arrow--next { right: calc(50% - 90px); }
  .team__dots { bottom: 20px; }

  .team-modal__body {
    grid-template-columns: 1fr;
    max-height: 90vh;
    overflow-y: auto;
  }
  .team-modal__photo {
    padding: 40px 24px 24px;
  }
  .team-modal__photo img {
    max-width: 200px;
    margin: 0 auto 20px;
  }
  .team-modal__photo-name { font-size: 24px; }
  .team-modal__content {
    padding: 32px 24px 40px;
    max-height: none;
  }
  .team-modal__highlight strong { font-size: 28px; }
  .team-modal__brief { font-size: 16px; }
}
</style>
